import cn from 'classnames';
import PriceContainer from '@nespresso/catalog-components-react/containers/PriceContainer';
import type { ComponentChildren } from 'preact';
import styles from './StickyPurchaseSection.module.scss';
import {
  getFixedHeaderZIndex,
  WITH_DATA_ATTRIBUTE_STICKY_CONTENT
} from '@/widgets/utils/layout.ts';
import {
  strikethroughPriceFromProductDiscount,
  type Product
} from '@/domain/Product.ts';
import usePriceFormatter from '@/widgets/hooks/usePriceFormatter';
import useRoute from '@/widgets/hooks/useRoute';
import useMarketConfig from '@/widgets/hooks/useMarketConfig';
import useMediaQuery from '@/widgets/hooks/useMediaQuery';
import useABTestVariant from '@/widgets/hooks/useABTestVariant';
import EXPERIMENTS from '@/constants/abExperiments';
import { useZoomScale } from '@/widgets/hooks/useZoomScale';
import { useTransition } from '@/widgets/hooks/useTransition';
import { trackVariantAction } from '@/widgets/services/TrackingService';

const ZOOM_THRESHOLD = 1.5; // 150% zoom in

export type StickyPurchaseSectionProps = {
  product: Product;
  isReadyToBeVisible?: boolean;
  top?: number;
  children?: ComponentChildren;
};

export default function StickyPurchaseSection({
  product,
  isReadyToBeVisible = true,
  top = 0,
  children
}: StickyPurchaseSectionProps) {
  const { isMobile } = useMediaQuery();
  const zoomScale = useZoomScale();
  const isV2 = useABTestVariant(
    EXPERIMENTS.STICKY_MACHINE_ADD_TO_CARD.V2_ENABLED
  );
  const formatPrice = usePriceFormatter();
  const { locale } = useRoute();
  const {
    financialSettings: { defaultCurrency: currencyCode }
  } = useMarketConfig();

  const strikethroughPrice = product.discount
    ? strikethroughPriceFromProductDiscount(
        product.pricing.price,
        product.discount
      )
    : undefined;

  const [shouldRender, isVisible] = useTransition(
    isReadyToBeVisible &&
      !product.isSubscriptionOnly &&
      zoomScale <= ZOOM_THRESHOLD
  );

  function trackClickOnA2C(e: MouseEvent) {
    if ((e.target as HTMLElement).closest('button')) {
      trackVariantAction(
        isV2
          ? EXPERIMENTS.STICKY_MACHINE_ADD_TO_CARD.V2_ENABLED
          : EXPERIMENTS.STICKY_MACHINE_ADD_TO_CARD.V1_ENABLED,
        'click'
      );
    }
  }

  return (
    shouldRender && (
      <section
        // Important: This data attribute is crucial to prevent issues when there are no fixed headers
        // but the purchase sticky element is active. Without it, the system might incorrectly detect
        // the sticky element as a fixed header, leading to an undesirable repositioning loop.
        // Note: Use the same data attribute for ALL sticky elements throughout the project.
        {...WITH_DATA_ATTRIBUTE_STICKY_CONTENT}
        role="presentation"
        onClick={trackClickOnA2C}
        class={cn(styles.container, {
          [styles.mobile]: isMobile,
          [styles.visible]: isVisible,
          [styles.hidden]: !isVisible
        })}
        style={{
          '--below-header-top': `${top}px`,
          '--below-header-zIndex': getFixedHeaderZIndex() - 1
        }}
      >
        <div>
          {!isMobile && <div>{product.name}</div>}
          <div>
            {(!isMobile || isV2) && (
              <PriceContainer
                locale={locale}
                currencyCode={currencyCode}
                price={product.pricing.price}
                strikethroughPrice={strikethroughPrice}
                priceFormatter={(v) => formatPrice(v)}
              />
            )}
            {children}
          </div>
        </div>
      </section>
    )
  );
}
